import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { saveAs } from "file-saver";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { useQuery } from "@apollo/client";

import { Button, Card, FormGroup, PageHeader } from "@cofano/cck";
import Grid from "@mui/material/Grid2";
import { Box, Typography } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";

import { DataFormat, Language, hasPluginPermission } from "imports/Utils";

import { DossierLink, RelationLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import { useDeclarationActions } from "view/declarations/utils/useDeclarationActions";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import InvoiceLinesTable from "view/declarations/InvoiceLinesTable";

import InvoicePdfPreview from "./components/pdf/InvoicePdfPreview";
import { isEditInvoiceAllowed } from "./utils/InvoiceUtils";

import { GetInvoiceDocument, GetInvoiceQuery } from "__generated__/view/invoices/graphql/invoices.queries.generated";
import { StatusType } from "__generated__/enums";
import { ChildProps, ChildPropsFromArray } from "types/commons";

type Invoice = ChildProps<GetInvoiceQuery, "invoice">;
type InvoiceLine = ChildPropsFromArray<Invoice, "lines">;
type Declaration = ChildProps<InvoiceLine, "declaration">;

type DeclarationGroup = {
	declaration: Declaration;
	lines: InvoiceLine[];
};

const InvoiceDetails = () => {

	const params = useParams();
	const navigate = useNavigate();
	const session = useStoreState(store => store.session);

	const { instance } = useCoastguard();

	const [ downloadLoading, setDownloadLoading ] = useState(false);

	const { data: invoiceData, refetch } = useQuery(GetInvoiceDocument, {
		variables: { id: Number(params.id) },
		skip: !params.id,
	});

	const invoice = invoiceData?.invoice ?? undefined;

	// The PDF is generated server-side from the invoice as it currently stands, so it has to be
	// regenerated alongside every refetch - editing a line does not change its URL.
	const [ previewVersion, setPreviewVersion ] = useState(0);
	const refetchInvoice = useCallback(() => {
		setPreviewVersion(version => version + 1);
		return refetch();
	}, [ refetch ]);

	const declarationGroups = useMemo(() => {
		const lines = (invoice?.lines ?? []).filter((line) => line != null);
		const groups = new Map<number, DeclarationGroup>();
		for (const line of lines) {
			if (!line.declaration) {
				continue;
			}
			const id = line.declaration.id;
			const existing = groups.get(id);
			if (existing) {
				existing.lines.push(line);
			} else {
				groups.set(id, { declaration: line.declaration, lines: [ line ] });
			}
		}
		// Same order as the generated PDF (see InvoicePdfExportService#makeInvoiceGroups): by charter
		// number, groups without a charter last. Compared like Java's String#compareTo rather than with
		// localeCompare, so the app and the document can't disagree on punctuation.
		return Array.from(groups.values()).sort((a, b) => {
			const left = a.declaration.charter?.charterNumber ?? null;
			const right = b.declaration.charter?.charterNumber ?? null;
			if (left === right) {
				return 0;
			}
			if (left == null) {
				return 1;
			}
			if (right == null) {
				return -1;
			}
			return left < right ? -1 : 1;
		});
	}, [ invoice?.lines ]);

	const onDownload = () => {
		if (!invoice) {
			return;
		}
		setDownloadLoading(true);
		instance.fetchBlob(`/invoices/export/download/${invoice.id}`, {
			method: "GET",
		}).then(async (blob: Blob) => {
			saveAs(blob, `Invoice-${invoice.id}.pdf`);
		}).finally(() => {
			setDownloadLoading(false);
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("INVOICE")}
					<Helmet title={intl.formatMessage({ id: "INVOICE" })} />
				</PageHeader.Title>

				{invoice && (
					<PageHeader.Actions>
						{isEditInvoiceAllowed(invoice, session) && (
							<Button onClick={() => navigate(`/view/invoices/${params.id}/edit`)}>
								{Language("EDIT")}
							</Button>
						)}
					</PageHeader.Actions>
				)}
			</PageHeader>

			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 6 }}>
					<Grid container spacing={2}>
						<Grid size={12}>
							<Card
								title={intl.formatMessage({ id: "DETAILS" })}
								actions={invoice ? [ {
									render: () => (
										<StatusChanger
											statusType={StatusType.Invoice}
											entity={invoice}
											refetch={refetchInvoice}
											showHistory />
									),
								} ] : undefined}
							>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, lg: 6 }}>
										<FormGroup label={intl.formatMessage({ id: "INVOICE_NUMBER" })} labelPlacement="left-center" overflow="hidden">
											{invoice?.invoiceNumber}
										</FormGroup>

										<FormGroup label={intl.formatMessage({ id: "INVOICE_DEBTOR" })} labelPlacement="left-center" overflow="hidden">
											<RelationLink relation={invoice?.debtor} />
										</FormGroup>

										<FormGroup label={intl.formatMessage({ id: "INVOICE_CREDITOR" })} labelPlacement="left-center" overflow="hidden">
											<RelationLink relation={invoice?.creditor} />
										</FormGroup>
									</Grid>

									<Grid size={{ xs: 12, lg: 6 }}>
										<FormGroup label={intl.formatMessage({ id: "TOTAL_EXCLUDE_VAT" })} labelPlacement="left-center" overflow="hidden">
											{DataFormat.price(invoice?.totalExcVAT)}
										</FormGroup>

										<FormGroup label={intl.formatMessage({ id: "INVOICE_EXPORT_TOTAL_VAT_AMOUNT" })} labelPlacement="left-center" overflow="hidden">
											{DataFormat.price(invoice?.totalVAT)}
										</FormGroup>

										<FormGroup
											label={intl.formatMessage({ id: (invoice?.totalAmount ?? 0) < 0 ? "TOTAL_TO_RECEIVE" : "TOTAL_TO_PAY" })}
											labelPlacement="left-center"
											overflow="hidden">
											{DataFormat.price(invoice?.totalAmount)}
										</FormGroup>

										{hasPluginPermission(session, "paymentTerms") && invoice?.paymentTerm && (
											<FormGroup label={intl.formatMessage({ id: "PAYMENT_TERM" })} labelPlacement="left-center" overflow="hidden">
												{`${invoice.paymentTerm.externalReference ?? ""} (${invoice.paymentTerm.days} ${intl.formatMessage({ id: "DAYS" })})`}
											</FormGroup>
										)}

										<FormGroup label={intl.formatMessage({ id: "INVOICE_DATE" })} labelPlacement="left-center" overflow="hidden">
											<FormattedDate date={invoice?.date ?? undefined} format="ll" />
										</FormGroup>
									</Grid>
								</Grid>
							</Card>
						</Grid>

						<Grid size={12}>
							<Card
								title={intl.formatMessage({ id: "INVOICE_LINES" })}
								disablePadding
							>
								<FormArrayContainer padding={declarationGroups.length > 0 ? undefined : 0}>
									{declarationGroups.length > 0 ? (
										declarationGroups.map(group => (
											<DeclarationGroupRow
												key={group.declaration.id}
												group={group}
												refetch={refetchInvoice} />
										))
									) : (
										<InvoiceLinesTable lines={invoice?.lines} />
									)}
								</FormArrayContainer>
							</Card>
						</Grid>
					</Grid>
				</Grid>

				{invoice && (
					<Grid size={{ xs: 12, md: 6 }}>
						<Box sx={{
							position: "sticky",
							top: "8px",
							height: "calc(100vh - 82px)",
							"& > .cck-card": {
								display: "flex",
								flexDirection: "column",
								height: "100%",
							},
							"& .MuiCollapse-root": {
								flex: 1,
								minHeight: 0,
							},
							"& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .cck-card-content": {
								height: "100%",
							},
						}}>
							<Card
								title={intl.formatMessage({ id: "DOCUMENT" })}
								disablePadding
								actions={[ {
									render: () => (
										<Button
											onClick={() => onDownload()}
											loading={downloadLoading}
											size="small"
											variant="outlined">
											{Language("DOWNLOAD")}
										</Button>
									),
								} ]}>
								<InvoicePdfPreview invoiceId={invoice.id} refreshKey={previewVersion} />
							</Card>
						</Box>
					</Grid>
				)}
			</Grid>
		</React.Fragment>
	);
};

interface DeclarationGroupRowProps {
	group: DeclarationGroup;
	refetch: () => void;
}

const DeclarationGroupRow = ({ group, refetch }: DeclarationGroupRowProps) => {
	const { declaration, lines } = group;
	const charter = declaration.charter ?? undefined;

	const { cardActions, dialogs } = useDeclarationActions({
		declaration,
		charter: charter ? { id: charter.id, type: charter.type } : undefined,
		invoiceGroupType: declaration.invoiceGroupType,
		refetch,
	});

	const total = lines.reduce((sum, line) => sum + (line.totalPriceExcVAT ?? 0), 0);

	const title = (
		<Typography
			textTransform="none"
			component="span"
			fontSize={13}
			fontWeight="bold">
			<DossierLink charter={charter} />
			{declaration.invoiceGroupType?.name && (
				<>
					{" - "}
					{declaration.invoiceGroupType.name}
				</>
			)}
		</Typography>
	);

	const subtitle = <span>{DataFormat.price(total)}</span>;

	return (
		<>
			<FormArrayRow
				title={title}
				subtitle={subtitle}
				onCustomActions={cardActions}
				disablePadding>
				<InvoiceLinesTable
					declaration={{ ...declaration, invoiceLines: lines }}
					refetch={refetch} />
			</FormArrayRow>
			{dialogs}
		</>
	);
};

export default InvoiceDetails;
